import { Mark, mergeAttributes } from '@tiptap/core';

export type SuggestionVariant = 'default' | 'tone' | 'fact';

export interface SuggestionMarkOptions {
  HTMLAttributes: Record<string, unknown>;
}

export interface SuggestionAttrs {
  sid: string;
  variant?: SuggestionVariant;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    suggestion: {
      /** Wrap the current selection in a suggestion mark with the given sid + variant. */
      setSuggestion: (attrs: SuggestionAttrs) => ReturnType;
      /** Drop the suggestion mark from the current selection. */
      unsetSuggestion: () => ReturnType;
    };
  }
}

const SID_ATTR = 'data-sid';
const VARIANT_ATTR = 'data-variant';
const VARIANTS: SuggestionVariant[] = ['default', 'tone', 'fact'];

function normalizeVariant(value: unknown): SuggestionVariant {
  return VARIANTS.includes(value as SuggestionVariant)
    ? (value as SuggestionVariant)
    : 'default';
}

/**
 * Inline suggestion as a TipTap Mark. Renders the marked range wrapped in
 * `<span class="ed-suggestion [variant]" data-sid="..." data-variant="...">`
 * so the overlay popover (SuggestionPopover + useDocSuggestionPopover) can
 * resolve it via `[data-sid]`. Mirrors the design reference's
 * `editor-overlays.jsx` SUGGESTIONS_BY_SID spans.
 *
 * Round-trips through `getHTML()` / `setContent()`: parseHTML hydrates any
 * pre-existing `.ed-suggestion[data-sid]` span (so legacy content with raw
 * spans keeps working) and reads the variant from either `data-variant` or
 * the trailing class token.
 */
export const SuggestionMark = Mark.create<SuggestionMarkOptions>({
  name: 'suggestion',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  inclusive: false,

  addAttributes() {
    return {
      sid: {
        default: '',
        parseHTML: (element) => element.getAttribute(SID_ATTR) ?? '',
        renderHTML: (attributes) => {
          const value = (attributes.sid as string | undefined) ?? '';
          if (!value) return {};
          return { [SID_ATTR]: value };
        },
      },
      variant: {
        default: 'default' as SuggestionVariant,
        parseHTML: (element) => {
          const explicit = element.getAttribute(VARIANT_ATTR);
          if (explicit) return normalizeVariant(explicit);
          // Hydrate from class list (editor-overlays.jsx pattern).
          const classes = element.className.split(/\s+/);
          for (const cls of classes) {
            if (cls === 'ed-suggestion') continue;
            if (VARIANTS.includes(cls as SuggestionVariant)) {
              return cls as SuggestionVariant;
            }
          }
          return 'default' as SuggestionVariant;
        },
        renderHTML: (attributes) => {
          const variant = normalizeVariant(attributes.variant);
          return { [VARIANT_ATTR]: variant };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span.ed-suggestion[${SID_ATTR}]`,
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const variant = normalizeVariant(
      (HTMLAttributes as Record<string, unknown>).variant ??
        (HTMLAttributes as Record<string, unknown>)[VARIANT_ATTR],
    );
    const className =
      variant === 'default' ? 'ed-suggestion' : `ed-suggestion ${variant}`;
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: className,
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setSuggestion:
        (attrs) =>
        ({ commands }) => {
          return commands.setMark(this.name, {
            sid: attrs.sid,
            variant: normalizeVariant(attrs.variant),
          });
        },
      unsetSuggestion:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});

export default SuggestionMark;
